/**
 * Identifier Validation Pipeline
 *
 * Validates serial/lot number values against a parametric configuration.
 * Steps (in order): min/max length -> prefix -> suffix -> regex -> character set -> checksum
 *
 * DRY Compliance: Shared between backend-wms and frontend.
 * Rule 15 Compliance: Pure function, no side effects.
 */

import {
  CharacterSet,
  ChecksumAlgorithm,
  type IdentifierValidationConfig,
  type IdentifierValidationResult,
} from '../types/wms/identifier-validation.types';

import { validateChecksum } from './checksum.util';

/** Maximum allowed regex execution time in milliseconds */
const REGEX_TIMEOUT_MS = 100;

/**
 * Execute a regex test with timeout protection against ReDoS.
 * Uses a synchronous character-budget approach: if the input exceeds
 * a safe length for unbounded regex, we reject it proactively.
 */
function safeRegexTest(pattern: string, value: string): { matched: boolean; error?: string } {
  try {
    const regex = new RegExp(pattern);
    // Budget-based protection: reject extremely long values to mitigate ReDoS
    const MAX_SAFE_INPUT_LENGTH = 10_000;
    if (value.length > MAX_SAFE_INPUT_LENGTH) {
      return { matched: false, error: 'Input value too long for regex validation' };
    }
    const start = Date.now();
    const matched = regex.test(value);
    const elapsed = Date.now() - start;
    if (elapsed > REGEX_TIMEOUT_MS) {
      return { matched: false, error: `Regex evaluation exceeded ${REGEX_TIMEOUT_MS}ms timeout` };
    }
    return { matched };
  } catch {
    return { matched: false, error: `Invalid regex pattern: ${pattern}` };
  }
}

/**
 * Detect patterns with nested quantifiers that cause catastrophic backtracking.
 * Matches: (a+)+, (a*)+, (a+)*, (?:a+)+ etc.
 */
const NESTED_QUANTIFIER_REGEX = /(\([^)]*[+*][^)]*\))[+*]|\([^)]*[+*][^)]*\)\{/;

/**
 * Validate that a regex pattern is syntactically valid AND safe from ReDoS.
 * Rejects patterns with nested quantifiers that cause catastrophic backtracking.
 * Used at rule creation time to reject dangerous patterns early.
 */
export function isValidRegexPattern(pattern: string): boolean {
  try {
    new RegExp(pattern);
    // Reject nested quantifiers (ReDoS vectors)
    if (NESTED_QUANTIFIER_REGEX.test(pattern)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Character set regex patterns */
const CHARACTER_SET_PATTERNS: Record<CharacterSet, RegExp> = {
  [CharacterSet.NUMERIC]: /^\d+$/,
  [CharacterSet.ALPHANUMERIC]: /^[a-zA-Z0-9]+$/,
  [CharacterSet.FULL_ASCII]: /^[\x20-\x7E]+$/, // printable ASCII
};

/**
 * Validate an identifier value against a parametric configuration.
 *
 * @param value - The identifier string to validate
 * @param config - The validation configuration
 * @returns Validation result with error details if invalid
 */
export function validateIdentifier(
  value: string,
  config: IdentifierValidationConfig
): IdentifierValidationResult {
  // Step 1: Min length
  if (config.min_length !== undefined && value.length < config.min_length) {
    return fail(
      config.error_message ??
        `Value must be at least ${config.min_length} characters (got ${value.length})`
    );
  }

  // Step 2: Max length
  if (config.max_length !== undefined && value.length > config.max_length) {
    return fail(
      config.error_message ??
        `Value must be at most ${config.max_length} characters (got ${value.length})`
    );
  }

  // Step 3: Prefix
  if (config.prefix && !value.startsWith(config.prefix)) {
    return fail(config.error_message ?? `Value must start with "${config.prefix}"`);
  }

  // Step 4: Suffix
  if (config.suffix && !value.endsWith(config.suffix)) {
    return fail(config.error_message ?? `Value must end with "${config.suffix}"`);
  }

  // Step 5: Regex pattern (with ReDoS protection)
  if (config.pattern) {
    const regexResult = safeRegexTest(config.pattern, value);
    if (regexResult.error) {
      return fail(regexResult.error);
    }
    if (!regexResult.matched) {
      return fail(config.error_message ?? `Value does not match pattern: ${config.pattern}`);
    }
  }

  // Step 6: Character set
  if (config.character_set) {
    const charsetRegex = CHARACTER_SET_PATTERNS[config.character_set];
    if (charsetRegex && !charsetRegex.test(value)) {
      return fail(
        config.error_message ?? `Value contains invalid characters for ${config.character_set}`
      );
    }
  }

  // Step 7: Checksum
  if (config.checksum_algorithm && config.checksum_algorithm !== ChecksumAlgorithm.NONE) {
    if (!validateChecksum(value, config.checksum_algorithm)) {
      return fail(
        config.error_message ?? `Checksum validation failed (${config.checksum_algorithm})`
      );
    }
  }

  return { valid: true };
}

/** Helper to create a failed validation result */
function fail(error: string): IdentifierValidationResult {
  return { valid: false, error };
}
